import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import LoadIcon from '../../../assets/icons/load';
import {
  DEFAULT_COUNT_LAST_TRANSACTION,
  MAX_COUNT_LAST_TRANSACTION,
} from '../../../constants';
import Table, { Column } from '../../../modules/table';
import { TransactionHistoryResponse } from '../../../services/api/dto/Transactions';
import { useAppDispatch } from '../../../store';
import { getTransactionsHistory } from '../../../store/actions/transactions-history';
import { RootState } from '../../../types';
import currencyFormat from '../../../utils/currencyFormat';
import { getTransactionsHistoryBadge } from '../../../utils/getStatusBadge';
import { getFormatDate, getSum } from '../../../utils/utils';
import { AVAILABLE_TRANSACTION_DETAILS } from '../../transaction-history';

import RowComponent from './row-component';

const Details = ({ data }: { data: TransactionHistoryResponse }) => {
  const agent = data.agent || '';
  const agentTin = data.agentTin || '';

  return (
    <>
      {agent}
      {agentTin && <span>{` ИНН: ${agentTin}`}</span>}
    </>
  );
};

const TRANSACTION_TYPE_MAP = {
  CREDIT: 'Заём',
  CREDIT_REPAYMENT: 'Заём',
  SALE: 'Продажа',
  PURCHASE: 'Покупка',
  PURCHASE_TRANSFER: 'Перевод',
  ISSUE: 'Выпуск',
  ISSUE_PAYMENT: 'Выпуск',
  EXCHANGE: 'Обмен',
  REDEEM: 'Погашение',
};

const COLUMNS = [
  {
    title: 'Операция',
    name: 'operation',
    headerStyle: {
      width: '13.7%',
    },
    dataGetter: (data: TransactionHistoryResponse) => (
      <>
        <LoadIcon type={data.type} />
        {TRANSACTION_TYPE_MAP[data.type]}
      </>
    ),
  },
  {
    title: 'Детали, контрагент',
    name: 'title',
    headerStyle: {
      width: '10%',
      flexGrow: '1',
    },
    dataGetter: (data: TransactionHistoryResponse) => data,
    Component: Details,
  },
  {
    title: ' Сумма',
    name: 'sum',
    headerStyle: {
      width: '13.5%',
    },
    dataGetter: (data: TransactionHistoryResponse) =>
      getSum(currencyFormat(data.value, false)),
  },
  {
    title: 'Дата и время',
    name: 'date',
    headerStyle: {
      width: '16%',
    },
    dataGetter: (data: TransactionHistoryResponse) =>
      getFormatDate(data.openDate),
  },
  {
    title: 'Статус',
    name: 'status',
    headerStyle: {
      width: '12.8%',
    },
    dataGetter: (data: TransactionHistoryResponse) => data,
    Component: ({ data }: { data: TransactionHistoryResponse }) =>
      getTransactionsHistoryBadge(data.status, data.type, false),
  },
] as Column<TransactionHistoryResponse>[];

const OperationsHistory = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const data = useSelector(
    (s: RootState) => s.transactionsHistory.data.history
  );

  useEffect(() => {
    dispatch(
      getTransactionsHistory({
        type: null,
        countItems: DEFAULT_COUNT_LAST_TRANSACTION,
      })
    );
  }, []);

  return (
    <Table
      count={MAX_COUNT_LAST_TRANSACTION}
      data={data}
      RowComponent={RowComponent}
      onRowClick={({
        id,
        type: transactionType,
        isHybrid,
        specialFields,
      }: {
        id: string;
        type: string;
        isHybrid?: boolean;
        specialFields?: {
          tokenId: string;
        };
      }) => {
        switch (transactionType) {
          case AVAILABLE_TRANSACTION_DETAILS.PURCHASE:
            history.push(`history/sign-info/${id}`, {
              id,
              isHistory: true,
            });
            break;
          case AVAILABLE_TRANSACTION_DETAILS.SALE:
            history.push(`history/selling-sign-info/${id}`, {
              id,
              isHistory: true,
            });
            break;
          case AVAILABLE_TRANSACTION_DETAILS.CREDIT:
            history.push(`credit/details/${id}`, {
              id,
              isHistory: true,
            });
            break;
          case AVAILABLE_TRANSACTION_DETAILS.CREDIT_REPAYMENT:
            history.push(`credit/details/${id}`, {
              id,
              isHistory: true,
            });
            break;
          case AVAILABLE_TRANSACTION_DETAILS.ISSUE_PAYMENT:
            history.push(`history/${isHybrid ? 'hybrid' : 'cfa'}-info/${id}`, {
              id,
              isHistory: true,
            });
            break;
          case AVAILABLE_TRANSACTION_DETAILS.ISSUE:
            history.push(`history/${isHybrid ? 'hybrid' : 'cfa'}-info/${id}`, {
              id,
              isHistory: true,
            });
            break;
          case AVAILABLE_TRANSACTION_DETAILS.EXCHANGE:
            history.push(`history/exchange-info/${id}`, {
              id,
              isHistory: true,
            });
            break;
          case AVAILABLE_TRANSACTION_DETAILS.REDEEM:
            history.push(`/assets/details/${specialFields!.tokenId}`, {
              id: specialFields!.tokenId,
            });
            break;
          default:
            history.push(`history/details/${id}`, { id });
            break;
        }
      }}
      columns={COLUMNS}
      isOperationHistory
    />
  );
};

export default OperationsHistory;
